// Angular
import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
// Libraries
import { Observable } from 'rxjs';
// Afidium
import { IMenuItem, IPopupMenuConfig } from 'libraries/component/popup-menu/popup-menu.component';
import { Yii } from "libraries/interfaces/Yii";
import { BookingResourceService } from 'libraries/service/resources/booking/bookingResource.service';
import { IExportParameters } from 'libraries/service/resources/booking/interfaces/IExportParameters';
import { ProcessingResourceService } from 'app_sasha/shared/resources/processings/processingResource.service';
import { ITaskRequest } from 'libraries/service/resources/model/processing/ITaskRequest';
import { ICreateProcessingRequest, ICreateProcessingResponse, IProcessingParameter } from 'libraries/service/resources/model/processing/ICreateProcessing';
import { ExportFormats, IApiExportResponse } from 'libraries/interfaces/IApiExportResponse';
import { IGetBookingsCommonResponse } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { LanguageService } from "libraries/service/language/language.service";

export enum ProcessingType {
    AcceptChanges = 'acceptChanges',
    Cancel = 'cancel',
    CsvExport = 'csvExport',
    Issue = 'issue',
    QualityCheck = 'qualityCheck',
    Synchronize = 'synchronize',
    SendEmail = 'sendEmail',
    SendSms = 'sendSms',
    Void = 'void',
    ExportAll = 'exportAll',
    CarbonExport = 'carbonExport',
}

export interface IProcessingState {
    type: ProcessingType | null;
    isLoading: boolean;
    isError: boolean;
    errorMessage?: string;
    successResponse?: IApiExportResponse;
}

export const PROCESSING_WORDING: { [key: string]: string } = {
    buttonLabel: Yii.t('wui-sasha-js', 'Traitements'),
    cancel: Yii.t('wui-shared-js', "Annuler les dossiers"),
    cancelling: Yii.t('wui-shared-js', 'Annulation des dossiers'),
    csvExport: Yii.t('wui-sasha-js', 'Export Excel'),
   exportAll: Yii.t('wui-sasha-js', 'Export'),
    csvExportError: Yii.t('wui-sasha-js', "L'export Excel de la sélection de dossiers a rencontré une erreur."),
    exportAllError: Yii.t('wui-sasha-js', "L'export Excel a rencontré une erreur."),
    csvExportLoading: Yii.t('wui-sasha-js', 'Export Excel en cours'),
    csvExportSuccess: Yii.t('wui-sasha-js', "L'export Excel de la sélection de dossiers a été correctement effectué."),
    exportAllSuccess: Yii.t('wui-sasha-js', "L'export Excel a été correctement effectué."),
    email: Yii.t('wui-shared-js', "Email"),
    issue: Yii.t('wui-shared-js', "Émettre les dossiers"),
    issuing: Yii.t('wui-shared-js', 'Émission des dossiers'),
    processingLoading: Yii.t('wui-shared-js', 'Récupération des dossiers en cours'),
    qualityCheck: Yii.t('wui-shared-js', "Vérifier les dossiers"),
    qualityChecking: Yii.t('wui-shared-js', 'Vérification des dossiers'),
    synchronize: Yii.t('wui-shared-js', "Actualiser les dossiers"),
    synchronizing: Yii.t('wui-shared-js', "Actualisation des dossiers"),
    sendEmail: Yii.t('wui-shared-js', "Envoyer des e-mail"),
    sendSms: Yii.t('wui-shared-js', "Envoyer des SMS"),
    sms: Yii.t('wui-shared-js', "Sms"),
    void: Yii.t('wui-shared-js', "Voider les dossiers"),
    voiding: Yii.t('wui-shared-js', 'Void des dossiers en cours'),

};

export const EXPORT_WORDING: { [key: string]: string } = {
    carbonExport: Yii.t('wui-shared-js', 'Export carbon'),
};

@Injectable()
export class ProcessingService {
    public searchParams: any;
    public get bookings(): IGetBookingsCommonResponse[] {
        return this.bookingsModel;
    }

    public set bookings(value: IGetBookingsCommonResponse[]) {
        this.bookingsModel = value;
    }

    /////////// SERVICE METHODS
    private bookingsModel: IGetBookingsCommonResponse[];
    constructor(
        private readonly bookingResource: BookingResourceService,
        private readonly processingResource: ProcessingResourceService,
        private readonly router: Router,
        private readonly languageService: LanguageService,
    ) { }

    /**
     * @method getMenuConfig
     * @description returns the treatment menu configuration
     */
    public getMenuConfig(): IPopupMenuConfig {
        return {
            buttonLabel: PROCESSING_WORDING.buttonLabel,
            items: [
                this.getMenuItemConfig(ProcessingType.SendEmail),
            ]
        };
    }

    /**
     * @method getMenuItemConfig
     * @description helper method used in TreatmentService.getMenuConfig to build IMenuItem config object
     */
    public getMenuItemConfig(actionName: ProcessingType, wording : {[key: string]: string} =  PROCESSING_WORDING): IMenuItem {
        return {
            label: wording[actionName],
            actionName
        };
    }

    /**
     * @method csvExport
     * @description call CSV exports API
     */
    public exportCsv(selection: IGetBookingsCommonResponse[]): Observable<IApiExportResponse> {
        const exportParameters: IExportParameters = {
            syscodes: selection.map((booking: IGetBookingsCommonResponse) => booking.syscode),
            format: ExportFormats.XLS
        };
        return this.bookingResource.export(exportParameters);
    }

    public async goToProcessingPage(processingPage: string, previousRoute?: string): Promise<void> {

        const myPreviousRoute: string = previousRoute || this.router.url;
        const navigationExtras: NavigationExtras = {
            queryParams: {
                myPreviousRoute
            }
        };

        // Router in service
        // noinspection JSIgnoredPromiseFromCall
        await this.router.navigate(
            [processingPage],
            navigationExtras
        );
    }

    public qualityCheckBooking(bookings: IGetBookingsCommonResponse[]): Observable<ICreateProcessingResponse<IProcessingParameter>> {
        const task: ITaskRequest[] = bookings.map((booking: IGetBookingsCommonResponse) => ({
            booking: {
                syscode: booking.syscode,
                code: booking.code,
                host: booking.host,
            }
        }));

        return this.processingResource
            .create<ICreateProcessingRequest<IProcessingParameter>, ICreateProcessingResponse<IProcessingParameter>, IProcessingParameter>({
                type: 'qualityCheck',
                task,
                title: PROCESSING_WORDING.qualityChecking,
                parameter: {
                    language: this.languageService.userLanguage,
                }
            });
    }

    public cancelBooking(bookings: IGetBookingsCommonResponse[]): Observable<ICreateProcessingResponse<IProcessingParameter>> {
        const task: ITaskRequest[] = bookings.map((booking: IGetBookingsCommonResponse) => ({ 'booking': { 'syscode': booking.syscode } }));
        return this.processingResource
            .create<ICreateProcessingRequest<IProcessingParameter>, ICreateProcessingResponse<IProcessingParameter>, IProcessingParameter>({
                type: 'cancel',
                task,
                title: PROCESSING_WORDING.cancelling,
                parameter: {
                    language: this.languageService.userLanguage,
                }
            });
    }

    public issueBooking(bookings: IGetBookingsCommonResponse[]): Observable<ICreateProcessingResponse<IProcessingParameter>> {
        const task: ITaskRequest[] = bookings.map((booking: IGetBookingsCommonResponse) => ({ 'booking': { 'syscode': booking.syscode } }));
        return this.processingResource
            .create<ICreateProcessingRequest<IProcessingParameter>, ICreateProcessingResponse<IProcessingParameter>, IProcessingParameter>({
                type: 'issue',
                task,
                title: PROCESSING_WORDING.issuing,
                parameter: {
                    language: this.languageService.userLanguage,
                }
            });
    }

    public voidBooking(bookings: IGetBookingsCommonResponse[]): Observable<ICreateProcessingResponse<IProcessingParameter>> {
        const task: ITaskRequest[] = bookings.map((booking: IGetBookingsCommonResponse) => ({ 'booking': { 'syscode': booking.syscode } }));
        return this.processingResource
            .create<ICreateProcessingRequest<IProcessingParameter>, ICreateProcessingResponse<IProcessingParameter>, IProcessingParameter>({
                type: 'void',
                task,
                title: PROCESSING_WORDING.voiding,
                parameter: {
                    language: this.languageService.userLanguage,
                }
            });
    }

    public synchronizeBooking(bookings: IGetBookingsCommonResponse[]): Observable<ICreateProcessingResponse<IProcessingParameter>> {
        const task: ITaskRequest[] = bookings
            .map((booking: IGetBookingsCommonResponse) => ({ 'booking': { 'code': booking.code, 'host': booking.host, 'syscode': booking.syscode } }));
        return this.processingResource
            .create<ICreateProcessingRequest<IProcessingParameter>, ICreateProcessingResponse<IProcessingParameter>, IProcessingParameter>({
                type: 'synchronize',
                task,
                title: PROCESSING_WORDING.synchronizing,
                parameter: {
                    language: this.languageService.userLanguage,
                }
            });
    }
}
